import { Controller } from "@hotwired/stimulus"
import { get, post } from "@rails/request.js"

// Connects to data-controller="quick-treatment-picker"
export default class extends Controller {
  connect() {
    this.shortcutsCache = new Map()
  }

  disconnect() {
    this.close()
  }

  async open(event) {
    if (event.shiftKey) return

    const isModifierClick = event.type === "click" && (event.ctrlKey || event.metaKey)
    const isDoubleClick = event.type === "dblclick"
    if (!isModifierClick && !isDoubleClick) return

    const trigger = event.currentTarget
    const context = trigger.dataset.quickTreatmentPickerContext
    const shortcutsUrl = trigger.dataset.quickTreatmentPickerShortcutsUrl
    if (!context || !shortcutsUrl) return

    event.preventDefault()
    event.stopPropagation()

    this.activeTrigger = trigger
    this.activeContext = context
    this.activeFace = trigger.dataset.quickTreatmentPickerFace
    this.activeToothId = trigger.dataset.quickTreatmentPickerToothId
    this.activeOdontogramId = trigger.dataset.quickTreatmentPickerOdontogramId
    this.activeCreateUrl = trigger.dataset.quickTreatmentPickerCreateUrl
    this.activeFallbackUrl = trigger.getAttribute("href")

    const shortcuts = await this.fetchShortcuts(shortcutsUrl)
    await this.renderPopover(shortcuts)
  }

  async fetchShortcuts(shortcutsUrl) {
    if (this.shortcutsCache.has(shortcutsUrl)) {
      return this.shortcutsCache.get(shortcutsUrl)
    }

    try {
      const response = await get(shortcutsUrl, { responseKind: "json" })
      if (!response.ok) return []

      const data = await response.json
      this.shortcutsCache.set(shortcutsUrl, data)
      return data
    } catch (error) {
      console.error("Error loading quick shortcuts:", error)
      return []
    }
  }

  async renderPopover(shortcuts) {
    const shortcutsHtml = shortcuts
      .map((shortcut) => {
        const icon = shortcut.icon_url
          ? `<img src="${this.escapeHtml(shortcut.icon_url)}" alt="" class="size-4 shrink-0 opacity-90" />`
          : `<span class="inline-block size-4 shrink-0 rounded bg-neutral-200"></span>`

        return `
          <button type="button"
            class="quick-treatment-picker-item flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-neutral-800 hover:bg-sky-50"
            data-quick-treatment-picker-service-id="${shortcut.id}"
            data-quick-treatment-picker-status-preset="${this.escapeHtml(shortcut.status_preset || "optional")}">
            ${icon}
            <span class="line-clamp-1">${this.escapeHtml(shortcut.name)}</span>
          </button>
        `
      })
      .join("")

    const html = `
      <div class="border-b border-neutral-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Atajo rápido
      </div>
      <div class="max-h-[280px] overflow-auto">${shortcutsHtml || '<div class="px-3 py-2 text-sm text-neutral-500">No hay atajos disponibles</div>'}</div>
      <div class="border-t border-neutral-200 p-2">
        <button type="button" class="quick-treatment-picker-open-form w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50">
          Abrir formulario completo
        </button>
      </div>
    `

    const popoverController = this.application.getControllerForElementAndIdentifier(this.element, "popover")
    if (!popoverController) return

    const popoverElement = await popoverController.showFor(this.activeTrigger, {
      html,
      placement: this.activeContext === "root" ? "right-start" : "bottom-start"
    })
    if (!popoverElement) return

    popoverElement.querySelectorAll(".quick-treatment-picker-item").forEach((button) => {
      button.addEventListener("click", () => this.applyShortcut(button.dataset))
    })

    const openFormButton = popoverElement.querySelector(".quick-treatment-picker-open-form")
    if (openFormButton) {
      openFormButton.addEventListener("click", () => this.openFallbackForm())
    }
  }

  async applyShortcut(dataset) {
    if (!this.activeCreateUrl || !this.activeToothId || !dataset.quickTreatmentPickerServiceId) return

    const serviceId = dataset.quickTreatmentPickerServiceId
    const statusPreset = dataset.quickTreatmentPickerStatusPreset || "optional"
    const status = this.mapStatusPreset(statusPreset)

    const body =
      this.activeContext === "root"
        ? {
            dental_root: {
              tooth_id: this.activeToothId,
              odontogram_id: this.activeOdontogramId,
              dental_service_id: serviceId,
              status: status
            }
          }
        : {
            tooth_detail: {
              tooth_id: this.activeToothId,
              odontogram_id: this.activeOdontogramId,
              dental_service_id: serviceId,
              status: status,
              face: this.activeFace ? [this.activeFace] : []
            }
          }

    try {
      const response = await post(this.activeCreateUrl, {
        body: JSON.stringify(body),
        contentType: "application/json",
        responseKind: "turbo-stream"
      })

      if (!response.ok) {
        console.error("Quick shortcut apply failed")
      }
    } catch (error) {
      console.error("Error applying quick shortcut:", error)
    } finally {
      this.close()
    }
  }

  openFallbackForm() {
    if (this.activeFallbackUrl) {
      window.Turbo.visit(this.activeFallbackUrl, { frame: "selected-form-content" })
    }
    this.close()
  }

  mapStatusPreset(statusPreset) {
    if (statusPreset === "existing") return "existente"
    return "requerido"
  }

  close() {
    const popoverController = this.application.getControllerForElementAndIdentifier(this.element, "popover")
    popoverController?.close()
  }

  escapeHtml(value) {
    return String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }
}
